import { useCallback, useEffect, useState } from "react";

import { fetchTopics, type Topic } from "@/lib/api";

interface TopicsState {
  topics: Topic[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads `GET /api/topics` on mount. Returns the list plus loading/error flags and a
 * `reload()` for manual refresh / retry. No polling yet — live updates arrive via the
 * WebSocket relay in a later step.
 */
export function useTopics() {
  const [state, setState] = useState<TopicsState>({
    topics: [],
    loading: true,
    error: null,
  });

  const load = useCallback((signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchTopics(signal)
      .then((topics) => {
        if (signal?.aborted) return;
        setState({ topics, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (signal?.aborted || (err as Error)?.name === "AbortError") return;
        setState({
          topics: [],
          loading: false,
          error: (err as Error)?.message ?? "Failed to load topics",
        });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  return { ...state, reload };
}
